import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RestService } from '../rest/rest.service';
import { RestOptions } from '../rest/rest-request';
import { Watchlist, WatchlistResponse } from '../../domain/public-api';
import { WatchlistMapperService } from './watchlist-mapper.service';

@Injectable({
  providedIn: 'root'
})
export class WatchlistRestService {

  constructor(private restService: RestService,
              private watchlistMapper: WatchlistMapperService) {}

  /**
   * Get all watchlists.
   *
   * @param includeQuotes Boolean to include quotes
   * @returns Observable for getting all watchlists
   */
  getAllWatchlists(includeQuotes: boolean): Observable<Array<Watchlist>> {
    return this.restService.getResource('watchList.watchlists').get<Array<WatchlistResponse>>({ params: { includeQuotes } })
      .pipe(map((response: Array<WatchlistResponse>): Array<Watchlist> => this.watchlistMapper.mapAllWatchlists(response)));
  }

  /**
   * Returns watchlist by ID
   *
   * @param id Watchlist ID
   * @returns Observable for getting watchlist with provided ID
   */
  getWatchlistById(id: number): Observable<Watchlist> {
    return this.restService.getResource('watchList.watchlistById').get<WatchlistResponse>({ pathVariables: { id } })
      .pipe(map((response: WatchlistResponse): Watchlist => this.watchlistMapper.mapWatchlist(response, true, false)));
  }

  /**
   * Returns fundamental watchlist by ID
   *
   * @param id Watchlist ID
   * @returns Observable for getting fundamental watchlist with provided ID
   */
  getWatchlistWithFundamentalsById(id: number): Observable<Watchlist> {
    const params = {
      pathVariables: { id },
      params: {
        quoteType: 'FUNDAMENTALS'
      },
      warningCategory: 'marketsFundamentals'
    };
    return this.restService.getResource('watchList.watchlistById').get<WatchlistResponse>(params)
      .pipe(map((response: WatchlistResponse): Watchlist => this.watchlistMapper.mapWatchlist(response, true, true)));
  }

  /**
   * Saves watchlist
   *
   * @param watchlist Watchlist to save
   * @returns backend's return value for update operation is 'void'
   */
  saveWatchlist(watchlist: WatchlistResponse): Observable<void> {
    return this.restService.getResource('watchList.watchlist').put<WatchlistResponse, void>(watchlist);
  }

  /**
   * Deletes watchlist
   *
   * @param watchlist Watchlist to delete
   * @returns backend's return value for delete operation is 'void'
   */
  deleteWatchlist(watchlist: Watchlist): Observable<void> {
    return this.restService.getResource('watchList.delete')
      .post<Watchlist, void>(undefined, { pathVariables: { id: watchlist.number } } as RestOptions );
  }
}

